import { amorphicStatic, supertypeClass } from '@haventech/amorphic';

import { Auth0Client, Auth0Error } from './Auth0Client';
import { AppConfig, AppConfigTypes } from '../config/AppConfig';
import { AuthOwner, Customer } from '../customer/Customer';
import { AuthenticatedPrincipal } from '../AuthenticatedPrincipal';
import { PrincipalCustomer } from '../PrincipalCustomer';
import { ActivatedRoute } from '@angular/router';
import { Account } from '../account/Account';
import { Identity } from '../account/identity/Identity';
import { RedisCache } from '../RedisCache';
import { serverOnly } from '../helpers';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

export enum Auth0Channel {
  HAVEN = 'HAVEN',
  COVERPATH = 'COVERPATH',
  MMD = 'MMD',
}

export enum Auth0ClientTypes {
  LOGIN = 'login',
  API = 'api',
}

export enum Auth0ConfKeyPrefixes {
  API_CLIENT_ID = 'AUTH0_API_CLIENT_ID',
  API_CLIENT_SECRET = 'auth0APIClientSecret',
  API_DOMAIN = 'AUTH0_API_DOMAIN',
  AUDIENCE = 'AUTH0_AUDIENCE',
  CLIENT_ALIAS = 'AUTH0_CLIENT_ALIAS',
  CLIENT_ID = 'AUTH0_CLIENT_ID',
  CLIENT_SECRET = 'auth0ClientSecret',
  CONNECTION = 'AUTH0_CONNECTION',
  CUSTOM_DOMAIN = 'AUTH0_CUSTOM_DOMAIN',
  DOMAIN = 'AUTH0_DOMAIN',
  REGISTRATION_CLIENT_ALIAS = 'AUTH0_REGISTRATION_CLIENT_ALIAS',
  REGISTRATION_CLIENT_ID = 'AUTH0_REGISTRATION_CLIENT_ID',
  REGISTRATION_CLIENT_SECRET = 'auth0RegistrationClientSecret',
  RESPONSE_TYPE = 'auth0ResponseType',
  SCOPE = 'auth0Scope',
  API_SCOPE = 'auth0APIScope',
}

export enum Auth0CacheKeys {
  ACCESS_TOKEN = 'auth0Token',
  ACCESS_TOKEN_24H_EXP = 86400,
}

@supertypeClass({ toClient: true, toServer: true })
export class Auth0Utils {
  public static parseAuthenticationFragment(fragment: string): Auth0Fragment {
    const paramList = fragment.split('&');
    const paramListSeparated = paramList.map((param) => {
      return param.split('=');
    });
    const paramMap = paramListSeparated.reduce((accum, current) => {
      accum[current[0]] = current[1];
      return accum;
    }, {});

    return {
      access_token: paramMap['access_token'],
      expires_in: paramMap['expires_in'],
      token_type: paramMap['token_type'],
      state: paramMap['state'],
      code: paramMap['code'],
    };
  }

  public static getAuth0Configuration(channel: Auth0Channel): Auth0Configs {
    const configuration = {
      apiDomain: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.API_DOMAIN}_${channel}`),
      apiClientId: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.API_CLIENT_ID}_${channel}`),
      audience: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.AUDIENCE}_${channel}`),
      clientAlias: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.CLIENT_ALIAS}_${channel}`),
      clientId: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.CLIENT_ID}_${channel}`),
      customDomain: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.CUSTOM_DOMAIN}_${channel}`),
      domain: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.DOMAIN}_${channel}`),
      responseType: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.RESPONSE_TYPE}_${channel}`),
      scope: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.SCOPE}_${channel}`),
      apiScope: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.API_SCOPE}_${channel}`),
      connection: amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.CONNECTION}_${channel}`),
    };

    return configuration;
  }

  public static getAuth0Secret(channel: Auth0Channel): string {
    return amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.CLIENT_SECRET}_${channel}`);
  }

  public static getAuth0RegistrationSecret(channel: Auth0Channel): string {
    return amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.REGISTRATION_CLIENT_SECRET}_${channel}`);
  }

  public static getAuth0APIClientSecret(channel: string): string {
    return amorphicStatic.config.nconf.get(`${Auth0ConfKeyPrefixes.API_CLIENT_SECRET}_${channel}`);
  }

  public static getAuth0ClientInfo(auth0Channel: Auth0Channel): Auth0ClientInfo {
    const config = Auth0Utils.getAuth0Configuration(auth0Channel);
    return {
      clientId: config.clientId,
      clientSecret: Auth0Utils.getAuth0Secret(auth0Channel),
    };
  }

  public static getAMCTokenNamespacePrefix(): string {
    const prefix: string = amorphicStatic.config.nconf.get('amcTokenNamespace');
    return prefix;
  }

  public static async loadPrincipalForEmailChange(newEmail: string): Promise<AuthenticatedPrincipal> {
    let principal = null;

    const query = Customer.getFromPersistWithQuery({
      newEmail: {
        $regex: new RegExp('^' + newEmail.toLowerCase().replace(/([^0-9a-zA-Z])/g, '\\$1') + '$'),
        $options: 'i',
      },
    });

    const principals = await query;

    if (principals.length > 1) {
      throw new Error(`More than one customer found for email: ${newEmail}`);
    } else {
      principal = principals[0];
    }

    return principal;
  }

  public static async isAuth0Enabled(): Promise<boolean> {
    return Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.AUTH0_ENABLED_HAVEN);
  }

  public static async isAuth0EnabledByProvider(provider: AppConfigTypes): Promise<boolean> {
    const appConfigs: Array<AppConfig> = await AppConfig.getFromPersistWithQuery({
      key: provider,
    });

    let appConfig: AppConfig = null;

    if (appConfigs.length === 0) {
      const enableAuth0: string = amorphicStatic.config.nconf.get(provider) === false ? 'false' : 'true';
      appConfig = await Auth0Utils.createDefaultAuth0Enabled(provider, enableAuth0);
    } else {
      appConfig = appConfigs[0];
    }

    return appConfig ? appConfig.getBooleanValue() : true;
  }

  private static async createDefaultAuth0Enabled(config: AppConfigTypes, enableAuth0: string): Promise<AppConfig> {
    const newAuth0Config: AppConfig = AppConfig.new(config, enableAuth0);
    await newAuth0Config.persistSave();

    return newAuth0Config;
  }

  public static async toggleAuth0Haven(): Promise<AppConfig> {
    return Auth0Utils.toggleAuth0Entry(AppConfigTypes.AUTH0_ENABLED_HAVEN);
  }

  public static async toggleAuth0CoverpathClient(): Promise<AppConfig> {
    return Auth0Utils.toggleAuth0Entry(AppConfigTypes.AUTH0_ENABLED_COVERPATH);
  }

  public static async toggleAuth0MMD(): Promise<AppConfig> {
    return Auth0Utils.toggleAuth0Entry(AppConfigTypes.AUTH0_ENABLED_MMD);
  }

  private static async toggleAuth0Entry(auth0Entry: AppConfigTypes): Promise<AppConfig> {
    const appConfigs: Array<AppConfig> = await AppConfig.getFromPersistWithQuery({
      key: auth0Entry,
    });

    let appConfig: AppConfig = null;

    if (appConfigs.length === 0) {
      const enableAuth0: string = amorphicStatic.config.nconf.get(auth0Entry) === false ? 'false' : 'true';
      appConfig = await Auth0Utils.createDefaultAuth0Enabled(auth0Entry, enableAuth0);
    } else {
      appConfig = appConfigs[0];
    }

    appConfig.value = (!appConfig.getBooleanValue()).toString();
    await appConfig.persistSave();

    return appConfig;
  }

  public static principalOwnerNeedsUpdating(principal: PrincipalCustomer, appOwnerConfig: AuthOwner): boolean {
    if (!principal || !appOwnerConfig) {
      throw new TypeError('principal and appOwnerConfig are required');
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Utils',
        function: 'principalOwnerNeedsUpdating',
        category: UICategory.request,
        context: { piiLevel: UIPiiLevelEnum.s2 },
        data: {
          customerEmail: principal.email,
          principalAuthOwner: principal.authOwner,
          appOwnerConfig: appOwnerConfig,
        },
      },
      'Checking if principal ownership needs to be updated'
    );

    return principal.authOwner !== appOwnerConfig;
  }

  public static getAuthOwner(isAuth0Enabled: boolean) {
    if (isAuth0Enabled) {
      return AuthOwner.AUTH0;
    } else {
      return AuthOwner.HAVEN;
    }
  }

  public static getAuth0ErrorFromUrl(route: ActivatedRoute): Auth0Error {
    const fragment = route.snapshot.fragment;
    if (fragment && fragment.indexOf('error') !== -1) {
      const fragmentSplit = fragment.split('&');
      const errorCode = fragmentSplit[0].split('=')[1];
      const errorDescription = fragmentSplit[1].split('=')[1];
      const mode =
        fragmentSplit.length > 2 && fragmentSplit[2].indexOf('mode') !== -1 ? fragmentSplit[2].split('=')[1] : null;
      const provider =
        fragmentSplit.length > 3 && fragmentSplit[3].indexOf('provider') !== -1 ? fragmentSplit[3].split('=')[1] : null;
      return {
        code: errorCode,
        text: errorDescription,
        mode: mode,
        provider: provider,
      };
    }
    return null;
  }

  // given a customer account, return the primary identity
  public static getPrimaryIdentityFromAccount(account: Account): Identity {
    return account.activeIdentities.find((identity) => identity.isPrimary && identity.isActive);
  }

  public static getActiveSecondaryIdentitiesFromAccount(account: Account): Identity[] {
    return account.activeIdentities.filter((identity) => !identity.isPrimary && identity.isActive);
  }

  public static getCustomerId(customer: Customer): string {
    return customer && customer.__id__ ? customer.__id__.split('-')[2] : null;
  }

  @serverOnly()
  public static async getAuth0TokenFromCache(channel: Auth0Channel): Promise<string> {
    const redisCache = new RedisCache();
    const response = await redisCache.getStringFromCache(Auth0CacheKeys.ACCESS_TOKEN + channel);
    return response;
  }

  @serverOnly()
  public static async setAuth0TokenInCache(token: string, channel: Auth0Channel): Promise<void> {
    const redisCache = new RedisCache();
    await redisCache.setStringInCacheWithExpiration(
      Auth0CacheKeys.ACCESS_TOKEN + channel,
      token,
      Auth0CacheKeys.ACCESS_TOKEN_24H_EXP
    );
  }

  @serverOnly()
  public static async deleteAuth0TokenInCache(channel: Auth0Channel): Promise<void> {
    const redisCache = new RedisCache();
    await redisCache.deleteFromCache(Auth0CacheKeys.ACCESS_TOKEN + channel);
  }

  public static async handleAuth0Token(channel: Auth0Channel): Promise<string> {
    try {
      const existingToken: string = await this.getAuth0TokenFromCache(channel);

      if (existingToken === null || existingToken === undefined) {
        const newToken: string = await Auth0Client.acquireNewAPIToken(channel);
        await this.setAuth0TokenInCache(newToken, channel);

        UILoggerSingleton.getInstance().info(
          {
            module: 'Auth0Utils',
            function: 'handleAuth0Token',
            category: UICategory.request,
          },
          'New Auth0 Token acquired and stored in Redis Cache. Using new token.'
        );
        return newToken;
      } else {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Auth0Utils',
            function: 'handleAuth0Token',
            category: UICategory.request,
          },
          'Auth0 Token exists in Redis Cache. Using existing token.'
        );
        return existingToken;
      }
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Auth0Utils',
          function: 'handleAuth0Token',
          category: UICategory.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
          },
        },
        'Error while acquiring Auth0Token.'
      );
      throw new Error(error.message);
    }
  }

  public static mapCustomerChannelToAuth0Channel(customerChannel: string): Auth0Channel {
    switch (customerChannel) {
      case 'Haven':
        return Auth0Channel.HAVEN;

      case 'LifeBridge':
        return Auth0Channel.MMD;

      case 'MMDirect':
        return Auth0Channel.MMD;

      case 'CAS':
      case 'MMOPM':
      case 'MMTRAD':
        return Auth0Channel.COVERPATH;

      default:
        UILoggerSingleton.getInstance().error(
          {
            module: 'Auth0Utils',
            function: 'mapCustomerChannelToAuth0Channel',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
              data: {
                channel: customerChannel,
              },
            },
          },
          'There was an issue mapping the Customer table channel to Auth0Channel'
        );
        throw new Error('Unable to identify Channel');
    }
  }
}

export interface Auth0Fragment {
  access_token: string;
  expires_in: string;
  token_type: string;
  state: string;
  code: string;
}

export interface Auth0Configs {
  apiDomain: string;
  apiClientId: string;
  audience: string;
  clientAlias: string;
  clientId: string;
  customDomain: string;
  domain: string;
  responseType: string;
  scope: string;
  apiScope: string;
  connection: string;
}

export interface Auth0ClientInfo {
  clientId: string;
  clientSecret: string;
}

export interface Auth0WellKnownOIDC {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  device_authorization_endpoint: string;
  userinfo_endpoint: string;
  mfa_challenge_endpoint: string;
  jwks_uri: string;
  registration_endpoint: string;
  revocation_endpoint: string;
  scopes_supported: string[];
  grant_types_supported?: string[];
  response_types_supported: string[];
  code_challenge_methods_supported: string[];
  response_modes_supported: string[];
  subject_types_supported: string[];
  id_token_signing_alg_values_supported: string[];
  id_token_encryption_alg_values_supported?: string[];
  id_token_encryption_enc_values_supported?: string[];
  token_endpoint_auth_methods_supported: string[];
  token_endpoint_auth_signing_alg_values_supported?: string[];
  claims_supported: string[];
  request_uri_parameter_supported: boolean;
  claims_parameter_supported?: boolean;
  request_parameter_supported?: boolean;
}
